import React, { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { Briefcase, Search, BookOpen } from 'lucide-react';
import WatchDemoButton from '@/components/tutorial/WatchDemoButton';
import { TUTORIAL_PATH } from '@/data/tutorial';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import Seo from '@/components/Seo';
import SubDashboard from '@/pages/dashboards/SubDashboard';
import OwnerDashboard from '@/pages/dashboards/OwnerDashboard';
import AccountNotices from '@/components/AccountNotices';
import AccountHoldNotice from '@/components/AccountHoldNotice';
import AccountRoleFix from '@/components/AccountRoleFix';
import ResendSetupEmail from '@/components/ResendSetupEmail';
import ContractorNav from '@/components/contractor/ContractorNav';


import { canBuyLeads, canPostWorkOrders } from '@/lib/roles';


const Dashboard: React.FC = () => {
  const { t, path, lang } = useI18n();
  const { profile, loading } = useAuth();

  /**
   * GENERAL CONTRACTOR VIEW SWITCH.  (Fix for "I created the account as a
   * contractor and it asked me to post my new job — it didn't give the option
   * to find jobs as well.")
   *
   * A GC does BOTH things on this marketplace:
   *   • posts work orders to find subs, and
   *   • buys leads to win work themselves.
   *
   * roles.ts has always agreed — canBuyLeads('gc') and canPostWorkOrders('gc')
   * are both true — but this page only ever rendered OwnerDashboard for a GC,
   * so the entire lead-buying half of their account was unreachable from the
   * dashboard. They were shown a "post a job" screen and nothing else.
   *
   * Both halves are now available behind a tab. The default is "Find jobs",
   * because that is what someone who signed up as a contractor came here to
   * do. Nothing about permissions changed — both capabilities already existed
   * and are still enforced server-side.
   */
  const [gcView, setGcView] = useState<'find' | 'post'>('find');

  if (loading) return <div className="py-24 text-center text-slate-500">{t('common.loading')}</div>;
  if (!profile) return <Navigate to={`${path('/login')}?next=${encodeURIComponent(path('/dashboard'))}`} replace />;

  const name =
    profile.company_name || `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.email;

  // A GC is the only role with two dashboards to switch between. Derived from
  // roles.ts rather than a hard-coded === 'gc' so the rule stays in one place.
  const showGcTabs = canBuyLeads(profile.user_type) && canPostWorkOrders(profile.user_type);

  const tabBase =
    'inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition-colors sm:flex-none';

  const buyer = canBuyLeads(profile.user_type);

  return (
    <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 sm:py-8 lg:px-8">
      <Seo title={t('nav.dashboard')} description={t('dash.welcome')} noindex />
      {buyer ? (
        /* CONTRACTOR: one short line, then straight to Credits → My Leads.
           The demo / guide buttons were removed from here (they do not help
           buy, view or manage leads); the tutorial is still in the footer. */
        <p className="mb-4 truncate text-sm font-semibold text-slate-500">
          {t('dash.welcome')} <span className="font-extrabold text-slate-900">{name}</span>
        </p>
      ) : (
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-400">{t('dash.welcome')}</p>
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">{name}</h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <WatchDemoButton variant="solid" placement="dashboard" audience="sub" />
            <Link
              to={path(TUTORIAL_PATH)}
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border-2 border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-800 hover:bg-slate-50"
            >
              <BookOpen className="h-4 w-4" /> {lang === 'es' ? 'Guía paso a paso' : 'Step-by-step guide'}
            </Link>
          </div>
        </div>
      )}
      {/* CONTRACTOR WORKSPACE TABS — Credits · My Leads · Find Leads ·
          Membership · Profile. */}
      {buyer && <ContractorNav />}

      {/* A refund/dispute hold blocks every unlock at the database level, so
          it must be explained above everything else on the dashboard. */}
      <AccountHoldNotice />


      {/*
        WRONG ACCOUNT TYPE RECOVERY. Renders only when the server confirms this
        account carries company registration data but was stored as a project
        owner — the signup race fixed in AuthContext. It is how the contractor
        who was shown "post a job" instead of "find jobs" gets their real
        account back without waiting for support.
      */}
      <AccountRoleFix />

      {/* Credits but never unlocked a lead → offer the setup email again (once). */}
      <ResendSetupEmail />


      {/* Homeowners see the account notices here; contractor dashboards
          (SubDashboard) render their own copy right under the credits bar. */}
      {!buyer && <AccountNotices />}

      {profile.user_type === 'subcontractor' && <SubDashboard />}
      {profile.user_type === 'homeowner' && <OwnerDashboard mode="project" />}

      {showGcTabs && (
        <>
          <div
            role="tablist"
            aria-label={lang === 'es' ? 'Vistas del panel' : 'Dashboard views'}
            className="mb-6 flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm sm:inline-flex sm:flex-row"
          >
            <button
              type="button"
              role="tab"
              aria-selected={gcView === 'find'}
              onClick={() => setGcView('find')}
              className={`${tabBase} ${
                gcView === 'find' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <Search className="h-4 w-4" />
              {lang === 'es' ? 'Buscar trabajos' : 'Find jobs'}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={gcView === 'post'}
              onClick={() => setGcView('post')}
              className={`${tabBase} ${
                gcView === 'post' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <Briefcase className="h-4 w-4" />
              {lang === 'es' ? 'Publicar trabajo' : 'Post work'}
            </button>
          </div>
          <p className="mb-6 text-sm text-slate-500">
            {gcView === 'find'
              ? lang === 'es'
                ? 'Compre contactos de proyectos y gane trabajo para su empresa.'
                : 'Buy project leads and win work for your own company.'
              : lang === 'es'
              ? 'Publique una orden de trabajo para encontrar subcontratistas. Publicar es gratis.'
              : 'Post a work order to find subcontractors. Posting is always free.'}
          </p>

          {gcView === 'find' ? <SubDashboard /> : <OwnerDashboard mode="workorder" />}
        </>
      )}
    </div>
  );
};

export default Dashboard;
